import React, { useEffect, useState } from "react";
import { supabase } from "../../shop/api/supabase";

const EMPTY_FORM = {
  name: "", slug: "", description: "", price: "",
  kolicina_na_stanju: "", category_id: "",
  tehnika: "", dimenzije: "", sifra: "",
  image_url: "", is_active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [orderStats, setOrderStats] = useState({}); // slug → { prodato, zaSlanje }

  useEffect(() => {
    async function load() {
      const { data: tenant } = await supabase.from("tenants").select("id").eq("domain", "ikone.rs").single();
      setTenantId(tenant.id);

      const [{ data: prods }, { data: cats }, { data: orders }] = await Promise.all([
        supabase.from("products").select("*, categories(name)").eq("tenant_id", tenant.id).order("created_at", { ascending: false }),
        supabase.from("categories").select("*").eq("tenant_id", tenant.id),
        supabase.from("orders").select("items, status, delivery_date").eq("tenant_id", tenant.id).neq("status", "cancelled"),
      ]);

      // Agregacija po slug-u
      const today = new Date().toISOString().split("T")[0];
      const stats = {};
      (orders || []).forEach(({ items, status, delivery_date }) => {
        (items || []).forEach(({ slug, kolicina }) => {
          if (!stats[slug]) stats[slug] = { prodato: 0, zaSlanje: 0 };
          if (status === "delivered") {
            stats[slug].prodato += kolicina;
          } else if (delivery_date && delivery_date >= today) {
            stats[slug].zaSlanje += kolicina;
          }
        });
      });

      setProducts(prods || []);
      setCategories(cats || []);
      setOrderStats(stats);
      setLoading(false);
    }
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const autoSlug = (name) =>
    name.toLowerCase()
      .replace(/š/g, "s").replace(/đ/g, "dj").replace(/č/g, "c")
      .replace(/ć/g, "c").replace(/ž/g, "z")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((f) => ({ ...f, name, slug: editId ? f.slug : autoSlug(name) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      tenant_id: tenantId,
      category_id: form.category_id || null,
      name: form.name,
      slug: form.slug,
      description: form.description,
      price: parseFloat(form.price),
      kolicina_na_stanju: parseInt(form.kolicina_na_stanju),
      images: form.image_url ? [form.image_url] : [],
      attributes: { tehnika: form.tehnika, dimenzije: form.dimenzije, sifra: form.sifra },
      is_active: form.is_active,
    };

    if (editId) {
      const { data } = await supabase.from("products").update(payload).eq("id", editId).select("*, categories(name)").single();
      setProducts((prev) => prev.map((p) => p.id === editId ? data : p));
    } else {
      const { data } = await supabase.from("products").insert(payload).select("*, categories(name)").single();
      setProducts((prev) => [data, ...prev]);
    }

    setForm(EMPTY_FORM);
    setEditId(null);
    setShowForm(false);
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name, slug: p.slug, description: p.description || "",
      price: p.price, kolicina_na_stanju: p.kolicina_na_stanju,
      category_id: p.category_id || "",
      tehnika: p.attributes?.tehnika || "",
      dimenzije: p.attributes?.dimenzije || "",
      sifra: p.attributes?.sifra || "",
      image_url: p.images?.[0] || "",
      is_active: p.is_active,
    });
    setEditId(p.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Obriši proizvod?")) return;
    await supabase.from("products").delete().eq("id", id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-loading">Učitavanje...</div>;

  return (
    <div className="admin-products">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Proizvodi ({products.length})</h2>
        {!showForm && (
          <button className="admin-btn admin-btn--primary" onClick={() => setShowForm(true)}>
            + Dodaj ikonu
          </button>
        )}
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <h3 className="admin-form__title">{editId ? "Izmeni ikonu" : "Nova ikona"}</h3>

          <div className="admin-form__grid">
            <div className="admin-form__field">
              <label>Naziv *</label>
              <input name="name" value={form.name} onChange={handleNameChange} required />
            </div>
            <div className="admin-form__field">
              <label>Slug (URL) *</label>
              <input name="slug" value={form.slug} onChange={handleChange} required />
            </div>
            <div className="admin-form__field">
              <label>Cena (RSD) *</label>
              <input name="price" type="number" value={form.price} onChange={handleChange} required />
            </div>
            <div className="admin-form__field">
              <label>Količina na stanju *</label>
              <input name="kolicina_na_stanju" type="number" value={form.kolicina_na_stanju} onChange={handleChange} required />
            </div>
            <div className="admin-form__field">
              <label>Kategorija</label>
              <select name="category_id" value={form.category_id} onChange={handleChange}>
                <option value="">— Bez kategorije —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="admin-form__field">
              <label>Šifra</label>
              <input name="sifra" value={form.sifra} onChange={handleChange} />
            </div>
            <div className="admin-form__field">
              <label>Tehnika</label>
              <input name="tehnika" value={form.tehnika} onChange={handleChange} />
            </div>
            <div className="admin-form__field">
              <label>Dimenzije</label>
              <input name="dimenzije" value={form.dimenzije} onChange={handleChange} />
            </div>
            <div className="admin-form__field admin-form__field--full">
              <label>URL slike</label>
              <input name="image_url" value={form.image_url} onChange={handleChange} placeholder="https://..." />
            </div>
            <div className="admin-form__field admin-form__field--full">
              <label>Opis</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} />
            </div>
            <div className="admin-form__field">
              <label className="admin-form__checkbox">
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                Vidljivo u prodavnici
              </label>
            </div>
          </div>

          <div className="admin-form__actions">
            <button type="submit" className="admin-btn admin-btn--primary">
              {editId ? "Sačuvaj izmene" : "Dodaj ikonu"}
            </button>
            <button type="button" className="admin-btn admin-btn--ghost" onClick={handleCancel}>
              Otkaži
            </button>
          </div>
        </form>
      )}

      <div className="admin-products__table">
        <div className="admin-products__thead">
          <span>Naziv</span>
          <span>Cena</span>
          <span>Ukupno</span>
          <span>Prodato</span>
          <span>Za slanje</span>
          <span>Neprodato</span>
          <span>Status</span>
          <span></span>
        </div>
        {products.map((p) => {
          const s = orderStats[p.slug] || { prodato: 0, zaSlanje: 0 };
          const ukupno = p.kolicina_na_stanju + s.prodato + s.zaSlanje;
          return (
            <div key={p.id} className="admin-products__row">
              <span className="admin-products__name">{p.name}</span>
              <span>{Number(p.price).toLocaleString("sr-RS")} RSD</span>
              <span>{ukupno}</span>
              <span>{s.prodato}</span>
              <span style={{ color: s.zaSlanje > 0 ? "#1a5a9a" : "inherit", fontWeight: s.zaSlanje > 0 ? 700 : 400 }}>
                {s.zaSlanje}
              </span>
              <span className={p.kolicina_na_stanju === 0 ? "admin-products__stock--out" : ""}>
                {p.kolicina_na_stanju}
              </span>
              <span className={`admin-products__active ${p.is_active ? "active" : "inactive"}`}>
                {p.is_active ? "Vidljivo" : "Skriveno"}
              </span>
              <span className="admin-products__actions">
                <button className="admin-btn admin-btn--sm" onClick={() => handleEdit(p)}>Izmeni</button>
                <button className="admin-btn admin-btn--sm admin-btn--danger" onClick={() => handleDelete(p.id)}>Obriši</button>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
